import { getEndpoints } from './config.js';
import { post } from './api.js';

/**
 * Lightswitch SDK Customer Portal
 * 
 * Handles customer portal functionality for subscription management.
 * Pure JavaScript implementation with no framework dependencies.
 */

/**
 * Create a customer portal session and redirect to it
 * @param {string} [returnUrl] - URL to redirect back to after portal session (defaults to current page)
 * @returns {Promise<string>} The portal URL
 */
const createCustomerPortal = async (returnUrl) => {
  try {
    // Default to current page if no return URL provided
    let finalReturnUrl = returnUrl || window.location.href;
    
    // Ensure the return URL is absolute and valid
    if (finalReturnUrl && !finalReturnUrl.startsWith('http')) {
      finalReturnUrl = `${window.location.origin}${finalReturnUrl}`;
    }
    
    // Validate that we have a proper URL
    try {
      new URL(finalReturnUrl);
    } catch (e) {
      console.warn('Invalid return URL, using current page:', finalReturnUrl);
      finalReturnUrl = window.location.href;
    }
    
    console.log('Creating customer portal with return URL:', finalReturnUrl);
    
    const endpoints = getEndpoints();
    console.log('Customer portal endpoint:', endpoints.customer_portal);
    
    const requestData = {
      return_url: finalReturnUrl
    };
    console.log('Sending request data:', requestData);
    
    let response;
    try {
      response = await post(endpoints.customer_portal, requestData);
      console.log('Customer portal response:', response);
    } catch (apiError) {
      console.error('API call failed:', apiError);
      console.error('API error details:', {
        message: apiError.message,
        status: apiError.status,
        stack: apiError.stack
      });
      throw apiError;
    }
    
    if (!response.url) {
      throw new Error('No portal URL received from server');
    }
    
    console.log('Portal URL received:', response.url);
    return response.url;
  } catch (error) {
    console.error('Failed to create customer portal:', error);
    throw error;
  }
};

/**
 * Create customer portal and open it in a new tab
 * @param {string} [returnUrl] - URL to redirect back to after portal session (defaults to current page)
 * @returns {Promise<void>}
 */
const redirectToCustomerPortal = async (returnUrl) => {
  try {
    console.log('redirectToCustomerPortal called with returnUrl:', returnUrl);
    const portalUrl = await createCustomerPortal(returnUrl);
    console.log('About to open portal URL:', portalUrl);
    
    // Validate the portal URL before opening
    if (!portalUrl || portalUrl === returnUrl) {
      throw new Error('Invalid portal URL received - got return URL instead of portal URL');
    }
    
    window.open(portalUrl, '_blank');
  } catch (error) {
    console.error('Failed to redirect to customer portal:', error);
    // Don't redirect to return URL on error - just throw
    throw error;
  }
};

/**
 * Initialize automatic DOM element binding for customer portal
 * Finds all elements with data-lightswitch-customer-portal attribute and binds click handlers
 */
const initCustomerPortalElements = () => {
  // Only run in browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // Find all elements with the data attribute
  const elements = document.querySelectorAll('[data-lightswitch-customer-portal]');
  
  elements.forEach(element => {
    // Avoid double-binding
    if (element.hasAttribute('data-lightswitch-bound')) {
      return;
    }
    
    // Get return URL from data attribute or use current page
    const returnUrlFromAttr = element.getAttribute('data-lightswitch-customer-portal');
    let returnUrl;
    
    if (returnUrlFromAttr) {
      // Convert relative path to full URL if needed
      if (returnUrlFromAttr.startsWith('http')) {
        returnUrl = returnUrlFromAttr;
      } else {
        returnUrl = `${window.location.origin}${returnUrlFromAttr}`;
      }
    } else {
      returnUrl = window.location.href;
    }
    
    // Add click handler
    const clickHandler = async (event) => {
      event.preventDefault();
      
      console.log('Customer portal button clicked, returnUrl:', returnUrl);
      
      // Store original text outside try block so it's accessible in catch
      const originalText = element.textContent;
      
      try {
        // Add loading state
        element.textContent = 'Loading...';
        element.disabled = true;
        
        console.log('Creating customer portal...');
        await redirectToCustomerPortal(returnUrl);
        
        // Restore original state after successful portal creation
        element.textContent = originalText;
        element.disabled = false;
      } catch (error) {
        // Restore original state
        element.textContent = originalText;
        element.disabled = false;
        
        // Show error to user
        console.error('Customer portal error:', error);
        
        // Handle specific error cases
        if (error.message && error.message.includes('401')) {
          alert('Please log in to access your account portal.');
        } else if (error.message && error.message.includes('400')) {
          alert('Complete a purchase first to access your billing portal.');
        } else {
          alert('Failed to open customer portal. Please try again.');
        }
      }
    };
    
    element.addEventListener('click', clickHandler);
    element.setAttribute('data-lightswitch-bound', 'true');
    
    console.log('Bound customer portal to element:', element);
  });
  
  if (elements.length > 0) {
    console.log(`Lightswitch: Bound customer portal to ${elements.length} element(s)`);
  }
};

/**
 * Initialize customer portal with automatic DOM binding and mutation observer
 * This should be called after SDK initialization to handle dynamically added elements
 */
const initCustomerPortal = () => {
  // Initial binding
  initCustomerPortalElements();
  
  // Set up mutation observer for dynamically added elements
  if (typeof window !== 'undefined' && typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      let shouldRebind = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node or its children have the data attribute
              if (node.hasAttribute && node.hasAttribute('data-lightswitch-customer-portal')) {
                shouldRebind = true;
              } else if (node.querySelectorAll) {
                const childElements = node.querySelectorAll('[data-lightswitch-customer-portal]');
                if (childElements.length > 0) {
                  shouldRebind = true;
                }
              }
            }
          });
        }
      });
      
      if (shouldRebind) {
        initCustomerPortalElements();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    console.log('Lightswitch: Customer portal mutation observer initialized');
  }
};

export { createCustomerPortal, initCustomerPortal, initCustomerPortalElements, redirectToCustomerPortal };
